<template>
  <div class="renderPage rcc-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="rcc-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-cash-register</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase rcc-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Cierres de Caja
      </v-toolbar-title>
      <v-spacer />
      <v-chip v-if="esAdmin" small color="blue-grey darken-2" dark class="font-weight-bold">
        <v-icon x-small left>mdi-shield-account</v-icon>Vista Admin
      </v-chip>
    </v-toolbar>

    <!-- FILTROS -->
    <v-card flat class="rcc-filter-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="auto" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3 pt-2 pb-3">
        <v-row dense align="center">
          <!-- Fecha Inicio -->
          <v-col cols="12" sm="6" :md="esAdmin ? 3 : 4">
            <v-menu v-model="menuFechaInicio" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fechaInicio"
                  label="Fecha Inicio"
                  prepend-inner-icon="mdi-calendar-start"
                  readonly outlined dense hide-details
                  class="rcc-search"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="fechaInicio" no-title scrollable @input="menuFechaInicio = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Fecha Fin -->
          <v-col cols="12" sm="6" :md="esAdmin ? 3 : 4">
            <v-menu v-model="menuFechaFin" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fechaFin"
                  label="Fecha Fin"
                  prepend-inner-icon="mdi-calendar-end"
                  readonly outlined dense hide-details
                  class="rcc-search"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="fechaFin" no-title scrollable @input="menuFechaFin = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Selector vendedor (solo Admin) -->
          <v-col v-if="esAdmin" cols="12" sm="6" md="3">
            <v-select
              v-model="usuarioSeleccionado"
              :items="usuarios"
              item-text="nombre"
              item-value="id"
              label="Cajero"
              prepend-inner-icon="mdi-account-outline"
              outlined dense hide-details clearable
              class="rcc-search"
            ></v-select>
          </v-col>

          <!-- Nombre cajero (solo no-admin) -->
          <v-col v-else cols="12" sm="6" md="4" class="d-flex align-center">
            <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'blue-grey darken-3'">mdi-account-outline</v-icon>
            <div>
              <div class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">Cajero</div>
              <div class="font-weight-bold text-body-2">{{ user.nombre }}</div>
            </div>
          </v-col>

          <!-- Botón Buscar -->
          <v-col cols="12" sm="6" :md="esAdmin ? 3 : 4">
            <v-btn color="blue-grey darken-2" dark depressed block height="40" class="rcc-btn font-weight-bold" :loading="loading" @click="buscarCierres">
              <v-icon left size="16">mdi-magnify</v-icon> Buscar Cierres
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </v-card>

    <!-- TABLA -->
    <v-card flat class="rcc-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="52" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3">
        <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-lock-check</v-icon>
        <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Historial de Cierres</span>
        <v-spacer />
        <v-chip v-if="cierres.length > 0" x-small color="blue-grey darken-2" dark class="font-weight-bold">
          {{ cierres.length }} cierre(s)
        </v-chip>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="cierres"
        :loading="loading"
        loading-text="Buscando cierres..."
        item-key="id"
        class="rcc-table"
      >
        <template v-slot:[`item.fechaCierre`]="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.fechaCierre) }}</span>
        </template>

        <template v-slot:[`item.montoApertura`]="{ item }">
          <span class="text-body-2">₡ {{ formatMoney(item.montoApertura) }}</span>
        </template>

        <template v-slot:[`item.montoTotalVentas`]="{ item }">
          <span class="font-weight-bold" :class="$vuetify.theme.dark ? 'teal--text text--lighten-2' : 'teal--text text--darken-1'">₡ {{ formatMoney(item.montoTotalVentas) }}</span>
        </template>

        <template v-slot:[`item.montoCierreCaja`]="{ item }">
          <span class="font-weight-black" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">₡ {{ formatMoney(item.montoCierreCaja) }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark style="min-width:36px;height:36px;padding:0;border-radius:4px" @click.stop="verDetalle(item)">
            <v-icon size="18">mdi-eye-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-cash-register</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No hay cierres para el período seleccionado.</p>
            <v-btn color="primary" depressed small @click="buscarCierres">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- DRAWER DETALLE CIERRE -->
    <v-navigation-drawer v-model="dialogDetalle" fixed right temporary width="520" class="rcc-drawer">
      <div class="d-flex flex-column fill-height" v-if="cierreSeleccionado">

        <!-- HEADER DRAWER -->
        <div class="rcc-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'rcc-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-cash-register</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">Resumen de Cierre</div>
            <div class="caption white--text" style="opacity:0.7">Detalle Histórico #{{ cierreSeleccionado.id }}</div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="dialogDetalle = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- CONTENIDO -->
        <div class="flex-grow-1 overflow-y-auto pa-5">

          <!-- Flujo de Caja -->
          <div class="rcc-section-label mb-3">
            <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-cash-flow</v-icon>
            <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Flujo de Caja</span>
          </div>
          <v-card flat class="rcc-section-card pa-4 mb-4">
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-body-2 font-weight-medium">Fondo Apertura</span>
              <span class="font-weight-bold">₡{{ formatMoney(cierreSeleccionado.montoApertura) }}</span>
            </div>
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-body-2 font-weight-medium">Entradas a Caja</span>
              <span class="font-weight-bold success--text">+ ₡{{ formatMoney(cierreSeleccionado.montoIngresos) }}</span>
            </div>
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-body-2 font-weight-medium">Salidas de Caja</span>
              <span class="font-weight-bold error--text">- ₡{{ formatMoney(cierreSeleccionado.montoSalidas) }}</span>
            </div>
            <v-divider class="my-2"></v-divider>
            <div class="d-flex justify-space-between align-center">
              <span class="font-weight-black text-body-1">Total Esperado</span>
              <span class="font-weight-black text-h6" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">₡{{ formatMoney(cierreSeleccionado.montoCierreCaja) }}</span>
            </div>
          </v-card>

          <!-- Desglose por Medio -->
          <div class="rcc-section-label mb-3">
            <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-credit-card-outline</v-icon>
            <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Desglose por Medio de Pago</span>
          </div>
          <v-card flat class="rcc-section-card pa-4 mb-4">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="success">mdi-cash</v-icon>
                <span class="text-body-2 font-weight-medium">Efectivo (₡)</span>
              </div>
              <span class="font-weight-bold">₡{{ formatMoney(cierreSeleccionado.montoEfectivo) }}</span>
            </div>
            <div v-if="cierreSeleccionado.montoEfectivoUSD > 0" class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="success">mdi-currency-usd</v-icon>
                <span class="text-body-2 font-weight-medium">Efectivo (USD)</span>
              </div>
              <span class="font-weight-bold">$ {{ formatMoney(cierreSeleccionado.montoEfectivoUSD) }}</span>
            </div>
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="primary">mdi-credit-card-outline</v-icon>
                <span class="text-body-2 font-weight-medium">Tarjeta</span>
              </div>
              <span class="font-weight-bold">₡{{ formatMoney(cierreSeleccionado.montoTarjeta) }}</span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="indigo">mdi-cellphone-wireless</v-icon>
                <span class="text-body-2 font-weight-medium">SINPE</span>
              </div>
              <span class="font-weight-bold">₡{{ formatMoney(cierreSeleccionado.montoSinpe) }}</span>
            </div>
          </v-card>

          <!-- Turno -->
          <div class="text-center">
            <v-chip small outlined class="font-weight-medium" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text text--darken-1'">
              <v-icon x-small left>mdi-clock-outline</v-icon>
              {{ formatDate(cierreSeleccionado.fechaApertura) }} → {{ formatDate(cierreSeleccionado.fechaCierre) }}
            </v-chip>
          </div>
        </div>

        <!-- FOOTER ACCIONES -->
        <div class="rcc-drawer-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'rcc-drawer-footer--dark' : ''">
          <v-btn text class="mr-3 font-weight-bold rcc-btn" @click="dialogDetalle = false">Cerrar</v-btn>
          <v-spacer />
          <v-btn color="primary" depressed class="px-8 font-weight-bold rcc-btn" @click="imprimirCierre(cierreSeleccionado)">
            <v-icon left size="16">mdi-printer</v-icon> Imprimir
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CashService } from "../services/CashService";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "ReporteCierresCaja",
  components: { Snackbar },
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      fechaInicio: today,
      fechaFin: today,
      menuFechaInicio: false,
      menuFechaFin: false,
      usuarioSeleccionado: null,
      usuarios: [],
      cierres: [],
      dialogDetalle: false,
      cierreSeleccionado: null,
      loading: false,
      snackText: "",
      snackValue: false,
      headers: [
        { text: "ID", value: "id", width: 70 },
        { text: "Fecha Cierre", value: "fechaCierre" },
        { text: "Apertura", value: "montoApertura" },
        { text: "Total Ventas", value: "montoTotalVentas" },
        { text: "Total Cierre", value: "montoCierreCaja" },
        { text: "Ver", value: "actions", sortable: false, align: "center", width: 80 },
      ],
    };
  },
  computed: {
    ...mapGetters(["user", "strCia"]),
    esAdmin() {
      if (!this.user) return false;
      const tipo = this.user.tipo !== undefined ? this.user.tipo : this.user.Tipo;
      return tipo == 1;
    },
  },
  created() {
    if (this.esAdmin) {
      this.cargarUsuarios();
    } else {
      this.usuarioSeleccionado = this.user.id;
    }
    this.buscarCierres();
  },
  methods: {
    async cargarUsuarios() {
      try {
        const res = await axios.get("api/Usuarios/getUsuariosByCia?cia=" + this.strCia);
        this.usuarios = res.data;
      } catch (e) {
        console.error("Error cargando usuarios:", e);
      }
    },

    async buscarCierres() {
      this.loading = true;
      this.cierres = [];
      try {
        const uid = this.esAdmin ? this.usuarioSeleccionado : this.user.id;
        this.cierres = await CashService.getCierresCaja(this.strCia, this.fechaInicio, this.fechaFin, uid);
      } catch (e) {
        this.snackText = "Error al buscar cierres: " + e.message;
        this.snackValue = true;
      } finally {
        this.loading = false;
      }
    },

    verDetalle(item) {
      this.cierreSeleccionado = item;
      this.dialogDetalle = true;
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleString("es-CR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    },

    formatMoney(val) {
      if (val == null) return "0.00";
      return Number(val).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    imprimirCierre(item) {
      const fechaActual = this.formatDate(new Date());
      const ticketHTML = `
      <html><head><title>Reporte Cierre de Caja</title>
      <style>
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 13px; color: #000; margin: 0; padding: 0; }
        .ticket { width: 100%; max-width: 320px; margin: 0 auto; padding: 10px; }
        .text-center { text-align: center; }
        .font-bold { font-weight: bold; }
        .title { font-size: 18px; font-weight: bold; margin-bottom: 5px; text-transform: uppercase; }
        hr { border: 0; border-top: 1px dashed #000; margin: 8px 0; }
        .flex-row { display: flex; justify-content: space-between; align-items: flex-end; margin: 4px 0; }
      </style></head>
      <body><div class="ticket">
        <div class="text-center">
          <div class="title">REPORTE DE CIERRE</div>
          <div>COMERCIO: ${this.strCia || 'N/A'}</div>
        </div>
        <hr>
        <div class="flex-row"><span class="font-bold">FECHA REPORTE:</span><span>${fechaActual}</span></div>
        <div class="flex-row"><span class="font-bold">INICIO TURNO:</span><span>${this.formatDate(item.fechaApertura)}</span></div>
        <div class="flex-row"><span class="font-bold">FIN TURNO:</span><span>${this.formatDate(item.fechaCierre)}</span></div>
        <div class="flex-row"><span class="font-bold">CIERRE ID:</span><span>#${item.id}</span></div>
        <hr>
        <div class="flex-row"><span>Fondo Apertura:</span><span>₡${this.formatMoney(item.montoApertura)}</span></div>
        <div class="flex-row"><span>Entradas a Caja:</span><span>₡${this.formatMoney(item.montoIngresos)}</span></div>
        <div class="flex-row"><span>Salidas de Caja:</span><span style="color:red">₡${this.formatMoney(item.montoSalidas)}</span></div>
        <hr>
        <div class="flex-row"><span>Ventas Efectivo:</span><span>₡${this.formatMoney(item.montoEfectivo)}</span></div>
        <div class="flex-row"><span>Ventas Tarjeta:</span><span>₡${this.formatMoney(item.montoTarjeta)}</span></div>
        <div class="flex-row"><span>Ventas Sinpe:</span><span>₡${this.formatMoney(item.montoSinpe)}</span></div>
        <hr>
        <div class="flex-row font-bold" style="font-size:16px;margin-top:10px">
          <span>TOTAL EN CAJA:</span><span>₡${this.formatMoney(item.montoCierreCaja)}</span>
        </div>
        <hr>
        <div class="text-center" style="margin-top:15px">
          <div class="font-bold">¡Excelente turno!</div>
          <div>*** REPORTE HISTÓRICO ***</div>
        </div>
      </div></body></html>`;

      let iframe = document.getElementById("printFrame");
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "printFrame";
        iframe.style.display = "none";
        document.body.appendChild(iframe);
      }
      const doc = iframe.contentWindow.document;
      doc.open(); doc.write(ticketHTML); doc.close();
      setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 500);
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.rcc-container {
  background-color: #f1f5f9;
  min-height: 100vh;
}
.theme--dark.rcc-container {
  background-color: #0f172a;
}

/* ===== TOOLBAR ===== */
.rcc-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.theme--dark .rcc-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
}
.rcc-toolbar-title {
  font-size: 0.95rem !important;
  letter-spacing: 1.5px;
}

/* ===== BUTTONS ===== */
.rcc-btn {
  border-radius: 4px !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem !important;
}

/* ===== FILTER & TABLE CARDS ===== */
.rcc-filter-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.theme--dark .rcc-filter-card {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.rcc-table-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* ===== SEARCH ===== */
.rcc-search >>> .v-input__slot {
  border-radius: 4px !important;
}

/* ===== TABLE ===== */
.rcc-table { background: transparent !important; }
.rcc-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.rcc-table >>> tbody tr:hover {
  background-color: rgba(55, 71, 79, 0.05) !important;
}
.theme--dark .rcc-table >>> tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* ===== DRAWER ===== */
.rcc-drawer >>> .v-navigation-drawer__content {
  background-color: #ffffff !important;
}
.theme--dark .rcc-drawer >>> .v-navigation-drawer__content {
  background-color: #0f172a !important;
}

/* ===== DRAWER HEADER ===== */
.rcc-drawer-header {
  background-color: #37474f;
  flex-shrink: 0;
}
.rcc-drawer-header--dark {
  background-color: #263238 !important;
}

/* ===== DRAWER FOOTER ===== */
.rcc-drawer-footer {
  background-color: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
.rcc-drawer-footer--dark {
  background-color: #1e293b !important;
  border-top: 1px solid rgba(255, 255, 255, 0.07) !important;
}

/* ===== SECTION LABEL ===== */
.rcc-section-label {
  display: flex;
  align-items: center;
}

/* ===== SECTION CARD ===== */
.rcc-section-card {
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 4px !important;
  background-color: #ffffff !important;
}
.theme--dark .rcc-section-card {
  border-color: rgba(255, 255, 255, 0.07) !important;
  background-color: #1e293b !important;
}
</style>
